import React, { useCallback, useEffect, useRef, useState } from 'react';
import SearchRounded from '@mui/icons-material/SearchRounded';
import { Box, CircularProgress, InputBase, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { v2Colors } from '../../design/theme';
import { searchProducts } from '../../services/commerce';

const DEBOUNCE_MS = 300;

export default function LandingSearch({ storeId, compact = false }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const runSearch = useCallback(
    async (term) => {
      const q = term.trim();
      if (!q) {
        setResults([]);
        setSearching(false);
        return;
      }
      setSearching(true);
      try {
        const res = await searchProducts({ q, storeId, limit: 10 });
        setResults(Array.isArray(res?.data) ? res.data : []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    },
    [storeId]
  );

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    const t = setTimeout(() => runSearch(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, runSearch]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const showPanel = open && query.trim().length > 0;

  return (
    <Box ref={wrapRef} sx={{ position: 'relative', mb: compact ? 0 : 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 2,
          mt: compact ? 0 : 1.5,
          py: compact ? 1 : 1.25,
          bgcolor: v2Colors.white,
          border: '1px solid',
          borderColor: v2Colors.borderMuted,
          borderRadius: 999,
        }}
      >
        <SearchRounded sx={{ color: v2Colors.textMuted, fontSize: 22, flexShrink: 0 }} />
        <InputBase
          fullWidth
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Try 'mutton', 'eggs', 'biryani cut'…"
          sx={{
            fontSize: '0.9rem',
            color: v2Colors.heading,
            '& input::placeholder': {
              color: v2Colors.textMuted,
              opacity: 1,
            },
          }}
          inputProps={{ 'aria-label': 'Search products' }}
        />
        {searching ? <CircularProgress size={18} thickness={5} /> : null}
      </Box>

      {showPanel ? (
        <Box
          sx={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 20,
            bgcolor: v2Colors.white,
            border: '1px solid',
            borderColor: v2Colors.borderMuted,
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(26, 19, 15, 0.12)',
            maxHeight: 280,
            overflowY: 'auto',
          }}
        >
          {!searching && results.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
              No products found
            </Typography>
          ) : null}
          {results.map((p) => {
            const id = p.id || p.documentId;
            const subtitle = [p.subCategory, p.aka].filter(Boolean).join(' · ');
            return (
              <Box
                key={id}
                role="button"
                tabIndex={0}
                onClick={() => {
                  setOpen(false);
                  setQuery('');
                  navigate(`/v2/catalog/${encodeURIComponent(id)}`);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setOpen(false);
                    setQuery('');
                    navigate(`/v2/catalog/${encodeURIComponent(id)}`);
                  }
                }}
                sx={{
                  px: 2,
                  py: 1.25,
                  cursor: 'pointer',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Typography variant="body2" fontWeight={600} noWrap>
                  {p.name}
                </Typography>
                {subtitle ? (
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {subtitle}
                  </Typography>
                ) : null}
              </Box>
            );
          })}
        </Box>
      ) : null}
    </Box>
  );
}
